import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { EntitlementsProvider } from '@wg/hooks';
import { AuthProvider } from '@wg/hooks/auth';
import { createBillingApi } from '@wg/api';
import { InstanceProvider, useInstance } from './context/InstanceContext';
import { ChatSessionProvider } from './context/ChatSessionContext';
import {
  ParentBridgeProvider,
  useParentBridge,
} from './context/ParentBridgeContext';
import {
  PageContextProvider,
  usePageContext,
} from './context/PageContextProvider';
import { ViewProvider, useView } from './context/ViewContext';
import { CheckoutProvider } from './context/CheckoutContext';
import { ItineraryProvider } from './context/ItineraryContext';
import { VoiceProvider } from './context/VoiceContext';
import { auth, httpClient } from './client';
import { useAuth } from './hooks/useAuth';
import logger from './utils/logger';
import Header from './components/Header';
import AccountBadge from './components/AccountBadge';
import BottomNav from './components/BottomNav';
import Footer from './components/Footer';
import HostActionBridge from './components/HostActionBridge';
import ItinerarySyncReporter from './components/ItinerarySyncReporter';
import JoinHandler from './components/JoinHandler';
import AuthView from './views/AuthView';
import ChatView from './views/ChatView';
import ItineraryView from './views/ItineraryView';
import BookingView from './views/BookingView';
import CheckoutView from './views/CheckoutView';
import './App.css';

// Mirrors what RN's EntitlementsContext does: poll /billing/v1/me/balance on
// auth + every 60s. The first call lazily issues the guest trial grant on
// the billing service (without it, the LiveKit worker rejects sessions with
// "no budget available"). Gated on isAuthenticated from useAuth — covers
// both initial guest creation AND post-deploy session recovery (when the
// auth core clears+recreates the session, this re-enables polling for the
// new user without remounting).
const EntitlementsLayer: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated } = useAuth();
  const billingApi = useMemo(() => createBillingApi(httpClient, logger), []);
  // visibilitychange — refresh balance when tab returns to foreground.
  const onForegroundReturn = (cb: () => void) => {
    const handler = () => {
      if (document.visibilityState === 'visible') cb();
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  };
  return (
    <EntitlementsProvider
      billingApi={billingApi}
      logger={logger}
      isAuthenticated={isAuthenticated}
      onForegroundReturn={onForegroundReturn}
    >
      {children}
    </EntitlementsProvider>
  );
};

const PageActionToasts: React.FC = () => {
  const { t: tr } = useTranslation();
  const { toasts, dismissToast } = usePageContext();
  if (toasts.length === 0) return null;
  return (
    <div className="wg-page-action-toasts" role="status" aria-live="polite">
      {toasts.map(t => (
        <div key={t.id} className="wg-page-action-toast">
          <span>{t.message}</span>
          <button
            type="button"
            aria-label={tr('common.dismiss')}
            onClick={() => dismissToast(t.id)}
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
};

const DESKTOP_AUTO_EXPAND_BREAKPOINT = 720;

const Shell: React.FC = () => {
  const { view } = useView();
  const { error, resolvedTheme } = useInstance();
  const { layoutMax, layoutMode, requestLayout, hostViewport } =
    useParentBridge();

  // Desktop default = expanded. Bootstrap caps this against
  // theme.panel.layoutMax, so partners who explicitly want the small
  // corner panel just set layoutMax='native' in their instance config.
  // Mobile keeps its full-screen sheet (the bootstrap short-circuits any
  // layout request below 640px), so we skip the request to avoid an
  // unnecessary postMessage round-trip.
  // Only fires once per mount so a manual collapse via the header toggle
  // sticks until the user re-expands.
  const didAutoExpandRef = useRef(false);
  useEffect(() => {
    if (didAutoExpandRef.current) return;
    if (!resolvedTheme) return;
    if (layoutMax === 'native') return;
    if (typeof window === 'undefined') return;
    if (window.innerWidth < DESKTOP_AUTO_EXPAND_BREAKPOINT) return;
    didAutoExpandRef.current = true;
    requestLayout('expanded');
  }, [resolvedTheme, layoutMax, requestLayout]);

  // Suppress the visible UI until the partner theme is in. Bootstrap, guest
  // session, billing, and theme resolve all run in parallel below — gating
  // here just controls *paint*, not work. Until `resolvedTheme` is set we
  // render nothing; the iframe stays transparent (see html[data-wg-preset]
  // rule in index.css). On the user's first sight, theme + chrome appear
  // together rather than the seed palette flashing into the partner one.
  //
  // ChatView keeps its own "Connecting…" copy for the still-bootstrapping
  // session case — that paint is correctly themed because we waited.
  if (!resolvedTheme) {
    return null;
  }
  // Centered-extended header: agent avatar + name pill float as an
  // overlay over the top of the chat scroll area; close/expand pin to
  // the top-right of the panel. There is no separate title bar — the
  // chat scroll extends to the panel top and content scrolls underneath
  // the floating identity overlay. Chat input becomes a floating
  // overlay at the bottom with a backdrop blur. Linear-left keeps the
  // legacy in-flow title strip and standard input row.
  const headerLayout = resolvedTheme?.headerLayout || 'centered-extended';
  const isPhone = !!hostViewport?.isMobile;
  return (
    <div
      className="wg-shell"
      data-wg-layout={layoutMode}
      data-wg-header-layout={headerLayout}
      data-wg-phone={isPhone ? 'true' : 'false'}
    >
      <div className="wg-panel-card">
        <Header />
        <main className="wg-main">
          {error && <div className="wg-banner wg-banner--error">{error}</div>}
          <HostActionBridge />
          <ItinerarySyncReporter />
          <JoinHandler />
          {view === 'chat' && <ChatView />}
          {view === 'itinerary' && <ItineraryView />}
          {view === 'booking' && <BookingView />}
          {view === 'checkout' && <CheckoutView />}
          {view === 'auth' && <AuthView />}
        </main>
        <BottomNav />
        <AccountBadge />
        <Footer />
      </div>
      <PageActionToasts />
    </div>
  );
};

const App: React.FC = () => {
  return (
    <ParentBridgeProvider>
      <AuthProvider core={auth}>
        <InstanceProvider>
          <ChatSessionProvider>
            <EntitlementsLayer>
              <PageContextProvider>
                <ViewProvider>
                  <ItineraryProvider>
                    <CheckoutProvider>
                      <VoiceProvider>
                        <Shell />
                      </VoiceProvider>
                    </CheckoutProvider>
                  </ItineraryProvider>
                </ViewProvider>
              </PageContextProvider>
            </EntitlementsLayer>
          </ChatSessionProvider>
        </InstanceProvider>
      </AuthProvider>
    </ParentBridgeProvider>
  );
};

export default App;
